import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { ClientLogo } from '../components/ClientLogo';
import { Button } from '../components/ui/button';
import { FolderOpen, Plus, Users, ArrowRight, Plugs } from '@phosphor-icons/react';
import CreateProjectDialog from '../components/CreateProjectDialog';
import JiraImportDialog from '../components/JiraImportDialog';
import AppFooter from '../components/AppFooter';

export default function ProjectsPage() {
  const { currentOrg, getOrgHeaders, API } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showJira, setShowJira] = useState(false);

  // Deep-link: /projects?import=jira opens the Jira importer straight away
  // (used by the sidebar "+" → Import from Jira option).
  useEffect(() => {
    if (searchParams.get('import') === 'jira') {
      setShowJira(true);
      searchParams.delete('import');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchProjects = async () => {
    if (!currentOrg) return;
    try {
      const { data } = await axios.get(`${API}/projects`, { withCredentials: true, headers: getOrgHeaders() });
      setProjects(data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchProjects(); }, [currentOrg]);
  useEffect(() => { const h = () => fetchProjects(); window.addEventListener('project-created', h); return () => window.removeEventListener('project-created', h); }, [currentOrg]);

  const canCreate = ['owner', 'admin', 'project_manager'].includes(currentOrg?.role);

  return (
    <div className="flex-1 overflow-auto" data-testid="projects-page">
      <div className="px-8 py-6 border-b border-zinc-200 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-medium mb-1">{currentOrg?.org_name}</p>
          <h1 className="font-['Outfit'] text-2xl font-bold text-zinc-900 tracking-tight">Projects</h1>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="import-jira-button" onClick={() => setShowJira(true)} className="h-9 text-sm">
              <Plugs size={16} className="mr-1.5" /> Import from Jira
            </Button>
            <Button data-testid="create-project-button" onClick={() => setShowCreate(true)} className="h-9 bg-blue-600 hover:bg-blue-700 text-white text-sm">
              <Plus size={16} className="mr-1" /> New Project
            </Button>
          </div>
        )}
      </div>
      <div className="p-8">
        {projects.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen size={48} className="mx-auto text-zinc-300 mb-4" />
            <h3 className="font-['Outfit'] text-lg font-semibold text-zinc-900 mb-2">No projects yet</h3>
            <p className="text-sm text-zinc-500 mb-6">Create your first project to get started</p>
            {canCreate && <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700 text-white"><Plus size={16} className="mr-1" /> Create Project</Button>}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map(p => (
              <button key={p.project_id} data-testid={`project-card-${p.project_id}`} onClick={() => navigate(`/projects/${p.project_id}`)}
                className="bg-white border border-zinc-200 rounded-lg p-5 text-left hover:bg-zinc-50 hover:border-zinc-300 transition-all duration-200 group">
                <div className="flex items-start justify-between mb-3">
                  {p.client ? (
                    <div className="flex items-center gap-2 min-w-0">
                      <ClientLogo client={p.client} size={40} rounded="rounded-md" />
                      <div className="min-w-0">
                        <span className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold block leading-tight">Client</span>
                        <span className="text-xs font-medium text-zinc-700 truncate block leading-tight" data-testid={`project-client-${p.project_id}`}>{p.client.name}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-md bg-blue-600 flex items-center justify-center text-white text-sm font-bold">{p.key?.substring(0, 2)}</div>
                  )}
                  <ArrowRight size={16} className="text-zinc-300 group-hover:text-zinc-500 transition-colors duration-200" />
                </div>
                <h3 className="font-['Outfit'] text-base font-semibold text-zinc-900 tracking-tight mb-1">{p.name}</h3>
                <p className="text-sm text-zinc-500 line-clamp-2 mb-3">{p.description || 'No description'}</p>
                <div className="flex items-center gap-3">
                  <Badge className="text-[10px] bg-zinc-100 text-zinc-500 border border-zinc-200">{p.key}</Badge>
                  <span className="text-xs text-zinc-400 flex items-center gap-1"><Users size={12} /> {p.member_count || 0} members</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <CreateProjectDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchProjects(); }} onImportJira={() => setShowJira(true)} />
      <JiraImportDialog open={showJira} onClose={() => { setShowJira(false); fetchProjects(); }} onOpenProject={(pid) => navigate(`/projects/${pid}`)} />
      <AppFooter />
    </div>
  );
}
